import { Component } from '@theme/component';

/**
 * @typedef {Object} FeedbackFormRefs
 * @property {HTMLElement} feedbackButton - The button to trigger the feedback form
 */

/**
 * A custom element that loads and displays a Medallia feedback form
 *
 * @extends {Component<FeedbackFormRefs>}
 */
class FeedbackFormComponent extends Component {
  requiredRefs = ['feedbackButton'];

  constructor() {
    super();
    this.scriptLoaded = false;
    this.scriptLoading = false;
    this._handleInteraction = this.handleInteraction.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    // Add click event listener to the button
    if (this.refs.feedbackButton) {
      this._handleClick = this.handleClick.bind(this);
      this.refs.feedbackButton.addEventListener('click', this._handleClick);
    }

    // Add interaction listeners for lazy loading
    if (!this.scriptLoaded) {
      ['mousemove', 'touchstart', 'scroll', 'keydown'].forEach((event) => {
        window.addEventListener(event, this._handleInteraction, { passive: true });
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.refs.feedbackButton && this._handleClick) {
      this.refs.feedbackButton.removeEventListener('click', this._handleClick);
    }

    // Clean up interaction listeners
    ['mousemove', 'touchstart', 'scroll', 'keydown'].forEach((event) => {
      window.removeEventListener(event, this._handleInteraction);
    });
  }

  /**
   * Handle user interaction to trigger script load
   */
  handleInteraction() {
    // Clean up interaction listeners immediately
    ['mousemove', 'touchstart', 'scroll', 'keydown'].forEach((event) => {
      window.removeEventListener(event, this._handleInteraction);
    });

    if (!this.scriptLoaded && !this.scriptLoading) {
      this.loadScript().catch((error) => {
        console.warn('Failed to pre-load feedback script on interaction:', error);
      });
    }
  }

  /**
   * Handle click event on the feedback button
   * @param {Event} event - The click event
   */
  async handleClick(event) {
    event.preventDefault();

    const formId = this.dataset.formId || '13949';

    try {
      // Load the script if not already loaded
      if (!this.scriptLoaded && !this.scriptLoading) {
        await this.loadScript();
      }

      // Wait for the SDK to be available
      await this.waitForSDK();

      // Execute the commands
      if (window.KAMPYLE_ONSITE_SDK) {
        window.KAMPYLE_ONSITE_SDK.loadForm(formId);
        window.KAMPYLE_ONSITE_SDK.showForm(formId);
      } else {
        console.warn('KAMPYLE_ONSITE_SDK is not available');
      }
    } catch (error) {
      console.error('Failed to load feedback form:', error);
    }
  }

  /**
   * Load the Medallia embed script
   */
  loadScript() {
    return new Promise((resolve, reject) => {
      if (this.scriptLoaded) {
        resolve();
        return;
      }

      this.scriptLoading = true;

      // Check if script is already in the DOM
      const existingScript = document.querySelector('script[src*="medallia.com"][src*="embed.js"]');
      if (existingScript) {
        this.scriptLoaded = true;
        this.scriptLoading = false;
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src =
        'https://resources.digital-cloud-west.medallia.com/wdcwest/282872/onsite/embed.js';
      script.charset = 'UTF-8';

      script.onload = () => {
        this.scriptLoaded = true;
        this.scriptLoading = false;
        resolve();
      };

      script.onerror = () => {
        this.scriptLoading = false;
        reject(new Error('Failed to load Medallia embed script'));
      };

      document.body.appendChild(script);
    });
  }

  /**
   * Wait for the KAMPYLE_ONSITE_SDK to be available
   */
  waitForSDK(maxAttempts = 50, interval = 100) {
    return new Promise((resolve, reject) => {
      let attempts = 0;

      const checkSDK = () => {
        if (window.KAMPYLE_ONSITE_SDK) {
          resolve();
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(checkSDK, interval);
        } else {
          reject(new Error('KAMPYLE_ONSITE_SDK did not become available'));
        }
      };

      checkSDK();
    });
  }
}

if (!customElements.get('feedback-form')) {
  customElements.define('feedback-form', FeedbackFormComponent);
}
